import {PaymentManager, PaymentSDKBase} from '../common';
import {CustomPaymentMethod, PaymentConfig, PaymentMethodChangeEvent} from '../types';

declare global {
    interface Window {
        ElsaPaymentSDK: typeof ElsaPaymentSDK;
    }
}

export class ElsaPaymentSDK extends PaymentSDKBase {
    private eventListeners: Map<string, Function[]> = new Map();

    constructor(config: PaymentConfig) {
        // Use base class constructor which handles validation and PaymentManager creation
        super(config, {
            showToasts: true,
            autoInjectStyles: true
        });
    }

    /**
     * Static method to create a payment form (for easier usage)
     */
    static async createPaymentForm(
        container: HTMLElement | string,
        config: PaymentConfig,
        options?: {
            onSuccess?: (paymentIntent: any) => void;
            onError?: (error: string) => void;
            onCancel?: () => void;
            onPaymentMethodChange?: (event: PaymentMethodChangeEvent) => void;
        }
    ): Promise<void> {
        const containerElement = typeof container === 'string'
            ? document.querySelector(container) as HTMLElement
            : container;

        if (!containerElement) {
            throw new Error('Container element not found');
        }

        const sdk = new ElsaPaymentSDK(config);
        await sdk.createPaymentForm(containerElement, options);
    }

    /**
     * Create and mount a payment form to a DOM element
     */
    async createPaymentForm(
        container: HTMLElement,
        options?: {
            onSuccess?: (paymentIntent: any) => void;
            onError?: (error: string) => void;
            onPaymentMethodChange?: (event: PaymentMethodChangeEvent) => void;
        }
    ): Promise<void> {
        try {
            // Update payment manager options if provided
            if (options) {
                this.paymentManager = new PaymentManager({
                    config: this.config,
                    onSuccess: options.onSuccess,
                    onError: options.onError,
                    onPaymentMethodChange: options.onPaymentMethodChange,
                    showToasts: true,
                    autoInjectStyles: true
                });
            }

            // Use the payment manager to render the complete form
            await this.paymentManager.renderToContainer(container);

        } catch (error) {
            console.error('Error creating payment form:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to create payment form';
            container.innerHTML = `
                <div style="
                    color: #dc3545;
                    padding: 15px;
                    background-color: #f8d7da;
                    border: 1px solid #f5c6cb;
                    border-radius: 8px;
                    text-align: center;
                ">
                    ${errorMessage}
                </div>
            `;

            if (options?.onError) {
                options.onError(errorMessage);
            }
        }
    }

    /**
     * Get currently selected payment method
     */
    getSelectedPaymentMethod(): CustomPaymentMethod | null {
        return this.paymentManager.getSelectedPaymentMethod();
    }

    /**
     * Initialize and mount payment elements manually
     */
    async initialize(): Promise<void> {
        await this.initializePayment();
    }

    /**
     * Mount Stripe elements to specific selectors
     */
    async mountElements(selectors: {
        paymentElementSelector: string;
        submitButtonSelector?: string;
    }): Promise<void> {
        await this.paymentManager.mountElements(selectors);
    }

    /**
     * Confirm payment
     */
    async confirmPayment(): Promise<{ success: boolean; paymentIntent?: any; error?: string }> {
        return await super.confirmPayment();
    }

    /**
     * Destroy the payment manager
     */
    destroy(): void {
        super.destroy();
        this.eventListeners.clear();
    }

    /**
     * Add event listener
     */
    on(event: string, callback: Function): void {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, []);
        }
        this.eventListeners.get(event)!.push(callback);
    }

    /**
     * Remove event listener
     */
    off(event: string, callback?: Function): void {
        if (!this.eventListeners.has(event)) {
            return;
        }

        if (callback) {
            const listeners = this.eventListeners.get(event)!;
            const index = listeners.indexOf(callback);
            if (index > -1) {
                listeners.splice(index, 1);
            }
        } else {
            this.eventListeners.delete(event);
        }
    }

    /**
     * Emit event
     */
    private emit(event: string, ...args: any[]): void {
        if (!this.eventListeners.has(event)) {
            return;
        }

        this.eventListeners.get(event)!.forEach(callback => {
            try {
                callback(...args);
            } catch (error) {
                console.error(`Error in event listener for ${event}:`, error);
            }
        });
    }
}

// Make it available globally
if (typeof window !== 'undefined') {
    window.ElsaPaymentSDK = ElsaPaymentSDK;
}

export default ElsaPaymentSDK;
